import { API_BASE_URL, ELECTRICITY_OFFER_TYPE, GAS_OFFER_TYPE } from 'config'

/** Redux and Kea */
import { kea } from 'kea'
import { put, call } from 'redux-saga/effects'
import { Validations } from 'validations'
import {
  csObjectToUnderscore,
  csObjectFromUnderscore,
  captureSentryException,
  getDatesYear,
  generateMediaUrl,
} from 'utils'

import PropTypes from 'prop-types'
import axios from 'axios'
import DataLogic from 'containers/App/logic'

import pick from 'lodash/pick'
import isEmpty from 'lodash/isEmpty'
import sortBy from 'lodash/sortBy'
import first from 'lodash/first'
import isArray from 'lodash/isArray'

/* eslint-disable camelcase */
const EQUIPMENT_ENDPOINT = `${API_BASE_URL}/public/simulator/equipment/`

const hiddenDivisionsPerOfferType = {
  [GAS_OFFER_TYPE]: ['Garagem'],
}

/**
 * Map simple object to select fcomponent
 * @param {*} param0
 */
const selectMapper = ({ id, name, value, ...item }) => ({
  ...item,
  label: name,
  value: id !== undefined ? id : value,
  id: value !== undefined ? value : id,
})

/**
 * Map energy classes to select
 * @param {*} param0
 */
export const selectMapperClasse = ({ classe_energetica, id, ...item }) => ({
  label: `${classe_energetica.name} ${getDatesYear(
    item.start_date,
    item.end_date
  )}`,
  value: id,
  ...item,
  classe_energetica,
  id,
})

// Select the default class or the first if default doesnt exist
const selectDefault = (equipmentconsumption_set) => {
  const defaultClass = equipmentconsumption_set.find(
    (item) => item.default === true
  )
  return defaultClass || first(equipmentconsumption_set)
}

/**
 * Selects the first classe in claeses
 * @param {*} param0
 */
export const selectFirstClass = ({ equipmentconsumption_set, ...item }) => ({
  ...item,
  classes:
    equipmentconsumption_set &&
    equipmentconsumption_set.map(selectMapperClasse),
  selectedClass: selectDefault(equipmentconsumption_set),
})

const fields = {
  hasSolarSystem: [Validations.isUndefined],
  hasSolarSystemYear: [Validations.hasSolarSystem, Validations.isUndefined],
  hasCentralAirConditioning: [Validations.isUndefined],
  centralHeatingOfferType: [
    Validations.hasCentralAirConditioning,
    Validations.isUndefined,
  ],
  centralAirConditioningEquipment: [
    Validations.hasCentralAirConditioning,
    Validations.isUndefined,
  ],
  centralAirConditioningEnergyClass: [
    Validations.hasCentralAirConditioning,
    Validations.isUndefined,
  ],
  carCharging: [],
}

export default kea({
  path: () => ['scenes', 'habitation'],

  actions: () => ({
    fetchData: (limit) => ({ limit }),
    setData: (data) => ({ data }),
    setInvalid: (invalid) => ({ invalid }),
    setDivision: (division) => ({ division }),
    setCurrentTab: (index) => ({ index }),
    setCurrentEquipments: (equipments) => ({ equipments }),
    setSvg: (svg) => ({ svg }),
    closeStartModal: () => ({}),
    setDataFetchError: (fetchDataError) => ({ fetchDataError }),
  }),

  // VARIABLES
  reducers: ({ actions }) => ({
    data: [
      {},
      PropTypes.object,
      {
        [actions.setData]: (state, payload) => ({ ...payload.data }),
      },
    ],

    svg: [
      null,
      PropTypes.any,
      {
        [actions.setSvg]: (_, payload) => payload.svg,
      },
    ],

    currentEquipments: [
      [],
      PropTypes.object,
      {
        [actions.setCurrentEquipments]: (_, payload) => [...payload.equipments],
      },
    ],

    loading: [
      true,
      PropTypes.bool,
      {
        [actions.fetchData]: () => true,
        [actions.setData]: () => false,
        [actions.setDataFetchError]: () => false,
      },
    ],

    fields: [fields, PropTypes.object, {}],

    invalid: [
      [],
      PropTypes.array,
      {
        [actions.setInvalid]: (_, payload) => [...payload.invalid],
      },
    ],

    currentDivision: [
      null,
      PropTypes.any,
      {
        [actions.setDivision]: (_, payload) => payload.division,
      },
    ],

    currentTab: [
      0,
      PropTypes.number,
      {
        [actions.setCurrentTab]: (_, payload) => payload.index,
      },
    ],

    error: [
      false,
      PropTypes.array,
      {
        [actions.fetchData]: () => false,
        [actions.setData]: () => false,
        [actions.setDataFetchError]: (_, payload) => payload.fetchDataError,
      },
    ],

    showStartModal: [
      true,
      PropTypes.bool,
      {
        [actions.closeStartModal]: () => false,
      },
    ],

    dataChanged: [
      false,
      PropTypes.bool,
      {
        [actions.setValues]: (state, payload) => {
          if (
            payload.municipality ||
            payload.offerType ||
            payload.adults ||
            payload.children ||
            payload.elderly ||
            payload.constructionYear ||
            payload.equipmentUtilizationProfile ||
            payload.electricCar !== undefined ||
            payload.electricCar !== null
          ) {
            return true
          } else {
            return false
          }
        },
      },
    ],
  }),

  takeLatest: ({ actions, workers }) => ({
    [actions.fetchData]: workers.fetchData,
    [actions.setData]: workers.fetchSvg,
    [actions.setDivision]: workers.fetchSvg,
    [actions.setValues]: workers.shouldRefreshData,
  }),

  start: function*() {
    const { fetchData, setDivision } = this.actions
    const data = yield this.get('data')
    const error = yield this.get('error')
    const dataChanged = yield this.get('dataChanged')

    const { profileFinished } = yield DataLogic.get('finished')

    yield put(setDivision(null))
    // https://sentry2.ubiwhere.com/ubiwhere/simulador-avancado/issues/3364/
    // Be very carefull when changing this start function because
    // We import this logic and the simple habitation logic in the profile logic
    // This creates a infinite loop of start everytime we change go to the profile the equipment start is engaged
    // This is a problem with kea itself
    // But we needed because of this issue https://gitlab.ubiwhere.com/adene-poupa-energia/adene-simulador-avancado/issues/69
    // For the shouldRefreshData worker to keep working when unmounted
    // Changing this code can make side effects so please be aware
    // When props don't exists it means it comes from an import so we dont do anything
    if (!this.props) {
      return
    }

    // https://sentry2.ubiwhere.com/ubiwhere/simulador-avancado/issues/3364/
    if (!profileFinished) {
      this.props.router.push('/')
    } else {
      if (isEmpty(data) || error || dataChanged) {
        yield put(fetchData(1))
      }
    }
  },

  selectors: ({ selectors }) => ({
    divisions: [
      () => [selectors.data],
      ({ divisions }) => divisions,
      PropTypes.any,
    ],

    currentOfferType: [
      () => [selectors.data],
      ({ currentEquipmentOfferType }) => currentEquipmentOfferType,
      PropTypes.any,
    ],

    divisionsSvgs: [
      () => [selectors.data],
      ({ divisionsSvgs }) => divisionsSvgs,
      PropTypes.any,
    ],

    firstDivision: [
      () => [selectors.data],
      ({ divisions }) => first(divisions),
      PropTypes.any,
    ],

    currentDivision: [
      () => [selectors.currentDivision],
      (division) => division,
      PropTypes.any,
    ],
  }),

  workers: {
    *fetchData(action) {
      const { setData, setValues, setDataFetchError } = this.actions

      const values = yield DataLogic.get('values')
      const offerTypeObject = yield DataLogic.get('offerType')

      let params = csObjectToUnderscore(
        pick(values, [
          'constructionYear',
          'equipmentUtilizationType',
          'equipmentUtilizationProfile',
          'offerType',
        ])
      )

      const { adults, children, elderly } = values
      params.habitants = adults + children + elderly || 0

      try {
        const response = yield call(axios.get, EQUIPMENT_ENDPOINT, { params })
        const data = csObjectFromUnderscore(response.data)

        const {
          offerTypes,
          solarSystemYears,
          centralHeating,
          houseDivisions,
          equipment,
          //suggestedEquipment,
          energyClasses,
          carChargingFrequencies,
          carChargingDurations,
          carChargingPowers,
        } = data

        let cleanData = {}

        let suggestedEquipment = data.suggestedEquipment

        const carObject = equipment.find(
          (el) => el.name === 'Carro Eletrico' && el['svg_key'] === 'el-carro'
        )
        if (carObject) {
          suggestedEquipment.push(carObject)
        }

        cleanData.carChargingFrequencies = carChargingFrequencies.map(
          selectMapper
        )
        cleanData.carChargingDurations = carChargingDurations.map(selectMapper)
        cleanData.carChargingPowers = carChargingPowers.map(selectMapper)

        const quantitiesLogic = (item) => {
          let qtn = item.t1_factor_quantity

          if (params.habitants > 2) {
            let nr = params.habitants - 1
            nr = nr > 6 ? 6 : nr // haha
            qtn = item[`t${nr}_factor_quantity`]
          }

          return qtn || 1
        }

        const quantities = (item) => ({
          ...item,
          quantity: quantitiesLogic(item, params.habitants),
        })

        cleanData.equipments = equipment.map(selectFirstClass).map(quantities)

        cleanData.divisions = sortBy(
          houseDivisions.map((division) => {
            division.disabled = false
            if (hiddenDivisionsPerOfferType[offerTypeObject.slug]) {
              if (
                hiddenDivisionsPerOfferType[offerTypeObject.slug].indexOf(
                  division.name
                ) !== -1
              ) {
                division.disabled = true
              }
            }
            return division
          }),
          'order'
        )

        // Fetch divisions svgs
        cleanData.divisionsSvgs = {}

        for (const division of cleanData.divisions) {
          const response = yield call(
            axios.get,
            generateMediaUrl(division.svg_image)
          )
          const { data } = response

          let cleanedSvg = data.replace(
            '<?xml version="1.0" encoding="UTF-8"?>',
            ''
          )
          cleanData.divisionsSvgs[division.id] = cleanedSvg
        }

        cleanData.offerTypes = sortBy(offerTypes.map(selectMapper), 'order')

        cleanData.solarSystemYears = sortBy(
          solarSystemYears.map(selectMapper),
          'order'
        )

        cleanData.centralHeatingEquipments = sortBy(
          centralHeating
            .map(selectMapper)
            .map(selectFirstClass)
            .map(quantities),
          'order'
        )

        cleanData.sugestedEquipment = sortBy(
          suggestedEquipment.map(selectFirstClass).map(quantities),
          'order'
        )

        // Duplicate equipments for each house division it belongs
        // https://gitlab.ubiwhere.com/adene-poupa-energia/adenepe-portal-publico/issues/129
        let duplicates = []
        cleanData.sugestedEquipment = cleanData.sugestedEquipment.map(
          (equipment) => {
            if (isArray(equipment.house_divisions)) {
              equipment.house_divisions.map((division, index) => {
                if (index) {
                  // Ignore the first
                  // Create a clone so we dont change the current object
                  // Set the new house division to the clone
                  // Add element to array
                  const equipmentClone = { ...equipment }
                  equipmentClone.house_divisions = division
                  duplicates.push(equipmentClone)
                }
                return division
              })

              // Set array to the first element
              equipment.house_divisions = equipment.house_divisions[0]
            }

            return equipment
          }
        )

        // Merge duplicates and singles
        cleanData.sugestedEquipment = [
          ...cleanData.sugestedEquipment,
          ...duplicates,
        ]

        // Duplicate central equipments for each house division it belongs
        // https://gitlab.ubiwhere.com/adene-poupa-energia/adenepe-portal-publico/issues/129
        duplicates = []
        cleanData.centralHeatingEquipments = cleanData.centralHeatingEquipments.map(
          (equipment) => {
            if (isArray(equipment.house_divisions)) {
              equipment.house_divisions.map((division, index) => {
                if (index) {
                  // Ignore the first
                  // Create a clone so we dont change the current object
                  // Set the new house division to the clone
                  // Add element to array
                  const equipmentClone = { ...equipment }
                  equipmentClone.house_divisions = division
                  duplicates.push(equipmentClone)
                }
                return division
              })

              // Set array to the first element
              equipment.house_divisions = equipment.house_divisions[0]
            }

            return equipment
          }
        )

        // Merge duplicates and singles
        cleanData.centralHeatingEquipments = [
          ...cleanData.centralHeatingEquipments,
          ...duplicates,
        ]

        // Energy classes
        cleanData.energyClasses = energyClasses
          .map(selectMapper)
          .sort((a, b) => {
            var nameA = a.label.toUpperCase()
            var nameB = b.label.toUpperCase()
            if (nameA < nameB) {
              return -1
            }
            if (nameA > nameB) {
              return 1
            }

            return 0
          })
          .sort((a, b) => {
            let plusA = a.label.substring(1, a.label.length)
            let plusB = b.label.substring(1, b.label.length)

            if (plusA > plusB) {
              return -1
            }

            if (plusA < plusB) {
              return 1
            }

            return 0
          })

        // Used to check if we need to refetch data
        cleanData.currentEquipmentOfferType = offerTypeObject

        // Hide svg elements depending on offerType
        if (offerTypeObject.value === 3) {
          cleanData.hiddenSvgItems = [
            'el-arca',
            'el-frigorifico',
            'el-maquina-lavar-loica',
            'el-forno',
            'el-tvbox-router',
            'el-televisao',
            'el-microondas',
            'el-maquina-lavar-roupa',
            'el-maquina-secar-roupa',
            'el-candeeiro-teto-cozinha',
            'el-exaustor',
            'el-ar-condicionado',
            'el-candeeiro-pe',
            'el-candeeiro-mesa',
            'el-candeeiro-teto',
            'el-candeeiro-pe',
            'el-coluna',
            'el-computador',
            'el-carro',
          ]
        }

        cleanData.defaultChargingDuration = carChargingDurations.find(
          (c) => c.default
        )
        cleanData.defaultChargingPower = carChargingPowers.find(
          (c) => c.default
        )
        cleanData.defaultChargingFrequency = carChargingFrequencies.find(
          (c) => c.default
        )

        if (offerTypeObject.slug === ELECTRICITY_OFFER_TYPE) {
          cleanData.hiddenSvgItems = []
        }

        const defaultValues = {
          equipments: cleanData.sugestedEquipment,
          hasSolarSystem: false,
          hasCentralAirConditioning: false,
          solarSystemYear: 1,
          carCharging: {
            frequency: cleanData.defaultChargingFrequency.id,
            duration: cleanData.defaultChargingDuration.value,
            power: cleanData.defaultChargingPower.value,
          },
        }

        yield put(setData({ ...cleanData }))
        yield put(setValues(defaultValues))
      } catch (error) {
        // 404 ? Redirect user
        if (error.response && error.response.status === 404) {
          if (this.props) {
            this.props.router.push('/')
          }
        } else {
          captureSentryException(error)
          yield put(setDataFetchError(error))
        }
      }
    },

    // Request svg
    *fetchSvg() {
      const { setSvg } = this.actions
      const currentDivision = yield this.get('currentDivision')

      try {
        const response = yield call(
          axios.get,
          generateMediaUrl(currentDivision.svg_image)
        )
        const { data } = response

        let cleanedSvg = data.replace(
          '<?xml version="1.0" encoding="UTF-8"?>',
          ''
        )
        yield put(setSvg(cleanedSvg))
      } catch (error) {}
    },

    /**
     * If offer type changes force the equipments to reload
     * Should solve the issue in
     * https://gitlab.ubiwhere.com/adene-poupa-energia/adene-simulador-avancado/issues/69
     * @param {*} action
     */
    *shouldRefreshData(action) {
      const { fetchData, setDivision } = this.actions
      const { profileFinished } = yield DataLogic.get('finished')
      const payload = action.payload
      if (profileFinished && payload.offerType) {
        yield put(fetchData(1))
        yield setDivision(null)
      }
    },
  },

  connect: {
    actions: [DataLogic, ['setValue', 'setValues', 'setFinished']],
    props: [DataLogic, ['values', 'settings', 'iframed']],
  },
})



// WEBPACK FOOTER //
// ./src/containers/SimpleHabitation/logic.js